import { ArrowDownOutlined, ArrowUpOutlined, ReloadOutlined, SettingOutlined } from "@ant-design/icons";
import { Avatar, Button, Card, Col, Empty, Flex, Row, Segmented, Spin, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import ClinicianSearch from "../../components/Compliance/ClinicianSearch.component";
import PulseCard from "../../components/Compliance/PulseCard.component";
import { useAppSelector } from "../../hooks/redux-hooks";
import {
  type ComplianceCoachingRow,
  type RuleCaptureRow,
  type TrendPoint,
  useGetComplianceDashboardQuery,
} from "../../service/compliance.service";
import { getRandomAvatarColor } from "../../utils/ui.utils";

const trendColor: Record<string, string> = {
  declining: "#b91c1c",
  stable: "#6b7280",
  improving: "#047857",
};

const statusMeta: Record<string, { color: string; bg: string; label: string }> = {
  needs_attention: { color: "#b91c1c", bg: "#fef2f2", label: "Needs attention" },
  watch: { color: "#92400e", bg: "#fef3c7", label: "Watch" },
  on_track: { color: "#047857", bg: "#ecfdf5", label: "On track" },
};

const DAYS_OPTIONS = [
  { label: "7d", value: 7 },
  { label: "30d", value: 30 },
  { label: "90d", value: 90 },
];

const BreakdownBar = ({ row }: { row: RuleCaptureRow }) => (
  <Flex vertical gap={4} style={{ minWidth: 160 }}>
    <Flex style={{ height: 10, borderRadius: 5, overflow: "hidden", background: "#f4f5f8" }}>
      <div style={{ width: `${row.pass_pct}%`, background: "#10b981" }} />
      <div style={{ width: `${row.partial_pct}%`, background: "#f59e0b" }} />
      <div style={{ width: `${row.fail_pct}%`, background: "#fca5a5" }} />
    </Flex>
    <Flex gap={8} style={{ fontSize: 10.5, color: "#6b7280" }}>
      <span>Pass {row.pass_pct}%</span>
      <span>Partial {row.partial_pct}%</span>
      <span>Fail {row.fail_pct}%</span>
    </Flex>
  </Flex>
);

const TrendSparkline = ({ points }: { points: TrendPoint[] }) => {
  if (!points.length) return null;
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  if (values.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No trend data yet — keep generating notes to populate the chart"
      />
    );
  }

  const width = 800;
  const height = 200;
  const margin = { top: 16, right: 16, bottom: 28, left: 36 };
  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;
  const min = 0;
  const max = 100;
  const xStep = points.length > 1 ? innerW / (points.length - 1) : innerW;

  const path = points
    .map((p, i) => {
      if (p.value === null) return null;
      const x = margin.left + i * xStep;
      const y = margin.top + innerH * (1 - (p.value - min) / (max - min));
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .filter(Boolean) as string[];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={220}
      style={{ display: "block" }}
    >
      <title>Documentation health over time</title>
      {[0, 25, 50, 75, 100].map((tick) => {
        const y = margin.top + innerH * (1 - tick / 100);
        return (
          <g key={tick}>
            <line x1={margin.left} x2={width - margin.right} y1={y} y2={y} stroke="#eef0f3" strokeWidth={1} />
            <text x={margin.left - 6} y={y + 4} fontSize={10} textAnchor="end" fill="#9ca3af">
              {tick}
            </text>
          </g>
        );
      })}
      <polyline points={path.join(" ")} fill="none" stroke="#4f46e5" strokeWidth={2.4} />
      {points.map((p, i) => {
        if (p.value === null) return null;
        const x = margin.left + i * xStep;
        const y = margin.top + innerH * (1 - (p.value - min) / (max - min));
        return <circle key={p.date} cx={x} cy={y} r={3} fill="#4f46e5" />;
      })}
      {points.map((p, i) => {
        if (i % Math.max(1, Math.floor(points.length / 6)) !== 0 && i !== points.length - 1) return null;
        const x = margin.left + i * xStep;
        return (
          <text key={`${p.date}-l`} x={x} y={height - 8} fontSize={10} textAnchor="middle" fill="#9ca3af">
            {p.label}
          </text>
        );
      })}
    </svg>
  );
};

const ClinicianDirectoryCard = ({ rows, onViewAll }: { rows: ComplianceCoachingRow[]; onViewAll: () => void }) => {
  const navigate = useNavigate();
  const preview = rows.slice(0, 5);
  return (
    <Card
      title={
        <Flex justify="space-between" align="center">
          <Flex vertical>
            <Typography.Text strong style={{ fontSize: 16 }}>
              Clinicians
            </Typography.Text>
            <Typography.Text style={{ color: "#6b7280", fontSize: 12, fontWeight: 400 }}>
              Search by name or browse — click any row for notes, transcripts, and compliance detail.
            </Typography.Text>
          </Flex>
          <Button type="link" onClick={onViewAll}>
            See all →
          </Button>
        </Flex>
      }
      style={{ marginBottom: 32 }}
    >
      <div style={{ marginBottom: 16 }}>
        <ClinicianSearch />
      </div>
      {preview.length === 0 ? (
        <Empty description="No clinicians yet" />
      ) : (
        <>
          {preview.map((row) => (
            <Flex
              key={row.clinician_id}
              align="center"
              gap={12}
              style={{ padding: "12px 0", borderBottom: "1px solid #eef0f3", cursor: "pointer" }}
              onClick={() => navigate(`/compliance/clinicians/${row.clinician_id}`)}
            >
              <Avatar style={{ backgroundColor: getRandomAvatarColor(row.display_name) }}>{row.avatar_initials}</Avatar>
              <Flex vertical flex={1}>
                <Typography.Text strong>
                  {row.display_name}
                  {row.specialty ? <span style={{ color: "#6b7280", fontWeight: 400 }}> — {row.specialty}</span> : null}
                </Typography.Text>
                <Flex gap={6} align="center" style={{ marginTop: 4 }}>
                  {typeof row.notes_reviewed === "number" ? (
                    <Typography.Text style={{ fontSize: 11, color: "#6b7280" }}>
                      {row.notes_reviewed} notes reviewed
                    </Typography.Text>
                  ) : null}
                  {row.top_issue_categories.map((cat) => (
                    <Tag key={cat}>{cat}</Tag>
                  ))}
                </Flex>
              </Flex>
              <Flex vertical align="flex-end" gap={2}>
                <Typography.Text strong>{row.issue_count}</Typography.Text>
                <Typography.Text style={{ fontSize: 11, color: "#6b7280" }}>issues</Typography.Text>
                <Tag
                  color={
                    trendColor[row.trend_label] === "#047857"
                      ? "green"
                      : trendColor[row.trend_label] === "#b91c1c"
                        ? "red"
                        : "default"
                  }
                >
                  {row.trend_label}
                  {typeof row.trend_delta_pts === "number"
                    ? ` ${row.trend_delta_pts > 0 ? "+" : ""}${row.trend_delta_pts}`
                    : ""}
                </Tag>
              </Flex>
            </Flex>
          ))}
          {rows.length > 5 && (
            <Flex justify="center" style={{ paddingTop: 16 }}>
              <Button type="link" onClick={onViewAll}>
                See all {rows.length} clinicians →
              </Button>
            </Flex>
          )}
        </>
      )}
    </Card>
  );
};

const ruleColumnsBase = (): ColumnsType<RuleCaptureRow> => [
  {
    title: "Rule",
    dataIndex: "rule_name",
    key: "rule",
    render: (_value, row) => (
      <Flex vertical>
        <Typography.Text strong>{row.rule_name}</Typography.Text>
        {row.custom_note_name ? (
          <Typography.Text style={{ fontSize: 11, color: "#6b7280" }}>from "{row.custom_note_name}"</Typography.Text>
        ) : null}
      </Flex>
    ),
  },
  {
    title: "Capture",
    dataIndex: "capture_rate",
    key: "capture",
    width: 130,
    render: (value: number, row) => {
      const tier = value < 33 ? "low" : value < 67 ? "mid" : "good";
      const color = tier === "low" ? "#b91c1c" : tier === "mid" ? "#b45309" : "#047857";
      return (
        <Typography.Text strong style={{ fontSize: 16, color }}>
          {value}%
          <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 4, fontWeight: 400 }}>n={row.sample_size}</span>
        </Typography.Text>
      );
    },
  },
  {
    title: "Breakdown",
    key: "breakdown",
    render: (_value, row) => <BreakdownBar row={row} />,
  },
  {
    title: "Trend",
    dataIndex: "delta_pts",
    key: "trend",
    width: 100,
    render: (delta: number | null) => {
      if (delta === null) return <Typography.Text style={{ color: "#9ca3af", fontSize: 12 }}>—</Typography.Text>;
      if (delta === 0) return <Typography.Text style={{ color: "#6b7280", fontSize: 12 }}>flat</Typography.Text>;
      const up = delta > 0;
      return (
        <Flex align="center" gap={4}>
          {up ? (
            <ArrowUpOutlined style={{ color: "#059669", fontSize: 11 }} />
          ) : (
            <ArrowDownOutlined style={{ color: "#dc2626", fontSize: 11 }} />
          )}
          <Typography.Text style={{ color: up ? "#059669" : "#dc2626", fontSize: 12, fontWeight: 500 }}>
            {Math.abs(delta)} pts
          </Typography.Text>
        </Flex>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 140,
    render: (value: keyof typeof statusMeta) => {
      const meta = statusMeta[value];
      return (
        <span style={{ background: meta.bg, color: meta.color, padding: "3px 9px", borderRadius: 999, fontSize: 12 }}>
          {meta.label}
        </span>
      );
    },
  },
];

const ComplianceDashboard = () => {
  const navigate = useNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);
  const [days, setDays] = useState<number>(30);

  const { data, isLoading, refetch, isFetching } = useGetComplianceDashboardQuery({ days });

  if (!userInfo?.is_compliance_enabled || !userInfo?.is_group_admin) {
    return <Navigate to="/home" replace />;
  }

  const updatedLabel = data?.updated_at ? new Date(data.updated_at).toLocaleString() : "—";

  const ruleColumns = ruleColumnsBase();

  const liveBody = data ? (
    <>
      <Row gutter={16} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title="Weighted score across every finding evaluated in this window. Pass=1.0, Partial=0.5, Fail=0.">
            <span>
              <PulseCard
                label="Documentation health"
                value={`${data.documentation_health.value}%`}
                meta={`Based on ${data.documentation_health.sample_size} findings`}
                delta={data.documentation_health.delta_pts}
              />
            </span>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <PulseCard
            label="Notes reviewed"
            value={data.notes_reviewed.value}
            meta={`${data.notes_reviewed.evaluated} evaluated · ${data.notes_reviewed.coverage_pct}% coverage`}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <PulseCard
            label="Unresolved issues"
            value={data.unresolved_issues.value}
            meta={`${data.unresolved_issues.fail_count} fail · ${data.unresolved_issues.partial_count} partial`}
            emphasis="danger"
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <PulseCard
            label={data.risk_summary.label}
            value={data.risk_summary.value}
            meta="Sessions flagged with risk-category findings"
            emphasis="danger"
          />
        </Col>
      </Row>

      <Card
        title={
          <Flex vertical>
            <Typography.Text strong style={{ fontSize: 16 }}>
              Rule capture
            </Typography.Text>
            <Typography.Text style={{ color: "#6b7280", fontSize: 12, fontWeight: 400 }}>
              How each configured rule performs across notes generated in the window. Sorted by lowest capture.
            </Typography.Text>
          </Flex>
        }
        style={{ marginBottom: 32 }}
      >
        <Table<RuleCaptureRow>
          columns={ruleColumns}
          dataSource={data.rule_capture}
          rowKey={(row) => `${row.custom_note_id ?? "none"}::${row.rule_id}`}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: "No findings recorded yet for this window." }}
          size="middle"
        />
      </Card>

      <Row gutter={16} style={{ marginBottom: 32 }}>
        <Col xs={24} md={12}>
          <Card
            title={
              <Flex vertical>
                <Typography.Text strong style={{ fontSize: 16 }}>
                  Top issue categories
                </Typography.Text>
                <Typography.Text style={{ color: "#6b7280", fontSize: 12, fontWeight: 400 }}>
                  Where unresolved findings cluster across the org.
                </Typography.Text>
              </Flex>
            }
            style={{ height: "100%" }}
          >
            {data.risk_documentation.length === 0 ? (
              <Empty description="No issue categories surfaced yet" />
            ) : (
              data.risk_documentation.map((row) => (
                <Flex
                  key={row.category}
                  justify="space-between"
                  align="center"
                  style={{ padding: "10px 0", borderBottom: "1px solid #eef0f3" }}
                >
                  <Flex vertical>
                    <Typography.Text strong>{row.label}</Typography.Text>
                    <Typography.Text style={{ fontSize: 11, color: "#6b7280" }}>
                      {row.fail_count} fail · {row.partial_count} partial
                    </Typography.Text>
                  </Flex>
                  <Typography.Text strong style={{ fontSize: 18, color: "#0b1020" }}>
                    {row.total}
                  </Typography.Text>
                </Flex>
              ))
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={
              <Flex vertical>
                <Typography.Text strong style={{ fontSize: 16 }}>
                  Documentation health over time
                </Typography.Text>
                <Typography.Text style={{ color: "#6b7280", fontSize: 12, fontWeight: 400 }}>
                  Weighted compliance score across all evaluated findings.
                </Typography.Text>
              </Flex>
            }
            style={{ height: "100%" }}
          >
            <TrendSparkline points={data.trend} />
          </Card>
        </Col>
      </Row>

      <ClinicianDirectoryCard rows={data.coaching} onViewAll={() => navigate("/compliance/clinicians")} />
    </>
  ) : null;

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh", textAlign: "left", padding: "40px" }}>
      <Flex justify="space-between" align="flex-start" style={{ marginBottom: 28 }}>
        <Flex vertical gap={4}>
          <div
            style={{
              marginBottom: 8,
              color: "#262626",
              fontWeight: 600,
              fontSize: 24,
              lineHeight: "29px",
              letterSpacing: "0.01em",
            }}
          >
            Compliance Overview
          </div>
          <div style={{ fontWeight: 500, fontSize: 14, lineHeight: "140%", color: "#6A7077" }}>
            Continuous review of every note your clinicians generate.
          </div>
          {data?.scope?.group_name ? (
            <Typography.Text style={{ color: "#9ca3af", fontSize: 12 }}>
              {data.scope.group_name} · {data.scope.clinicians_in_group} clinicians · {data.scope.notes_evaluated} notes
              evaluated in window
            </Typography.Text>
          ) : null}
        </Flex>
        <Flex vertical align="flex-end" gap={4}>
          <Flex align="center" gap={8}>
            <Segmented options={DAYS_OPTIONS} value={days} onChange={(v) => setDays(Number(v))} disabled={isFetching} />
            <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()} loading={isFetching}>
              Refresh
            </Button>
            <Button type="primary" icon={<SettingOutlined />} onClick={() => navigate("/compliance/settings")}>
              Manage Rules
            </Button>
          </Flex>
          <Typography.Text style={{ fontSize: 11, color: "#9ca3af" }}>Updated {updatedLabel}</Typography.Text>
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex justify="center" style={{ padding: 64 }}>
          <Spin />
        </Flex>
      ) : !data ? (
        <Empty description="No compliance data yet" />
      ) : (
        liveBody
      )}
    </div>
  );
};

export default ComplianceDashboard;
